import React, { useEffect, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Checkbox, Input } from '@roger/ui-components';
import { UserCog } from 'lucide-react';
import { updateUser } from '../../../services/api';
import { useStore } from '../../../store';
import { formatUsPhoneEntry, isCompleteUsPhone } from '../../../utils/intake';
import { useShallow } from 'zustand/react/shallow';

// This section only renders for a viewer who administers users, which is
// exactly who the backend authorizes to write a name, so the name is always
// editable here. Roger-employee status is the one field that stays super-user
// only.
//
// A name is required only when it is being edited, which saveIfDirty checks
// rather than the schema: a member whose stored name is blank must still be
// reachable for a phone-only edit.
const schema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  // Optional, so blank passes; anything present must be a full US number.
  phoneNumber: z
    .string()
    .refine(isCompleteUsPhone, 'Enter a 10-digit US phone number'),
  isRogerEmployee: z.boolean(),
});

type FormData = z.infer<typeof schema>;

export type EditUserInfoHandle = {
  saveIfDirty: () => Promise<void>;
};

type EditUserInfoSectionProps = {
  userId: string;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  phoneNumber: string | null | undefined;
  isRogerEmployee: boolean;
  /**
   * Whether the viewer may edit Roger-employee status. Off for User Manager
   * admins, who may edit a member's name and phone number but must not grant
   * them Roger-employee status.
   */
  canEditRogerEmployee?: boolean;
};

export const EditUserInfoSection = React.memo(
  React.forwardRef<EditUserInfoHandle, EditUserInfoSectionProps>(
    function EditUserInfoSection(
      {
        userId,
        firstName,
        lastName,
        phoneNumber,
        isRogerEmployee,
        canEditRogerEmployee = true,
      },
      ref
    ) {
      const { showError } = useStore(
        useShallow(state => ({ showError: state.showError }))
      );

      const {
        register,
        control,
        reset,
        trigger,
        getValues,
        setError,
        formState: { errors, isDirty, dirtyFields },
      } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          phoneNumber: phoneNumber ?? '',
          isRogerEmployee: isRogerEmployee,
        },
      });

      useEffect(() => {
        reset({
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          phoneNumber: phoneNumber ?? '',
          isRogerEmployee: isRogerEmployee,
        });
      }, [firstName, lastName, phoneNumber, isRogerEmployee, reset]);

      useImperativeHandle(
        ref,
        () => ({
          saveIfDirty: async () => {
            if (!isDirty) return;
            const isValid = await trigger();
            if (!isValid) throw new Error('Validation failed');
            const data = getValues();
            const nameEdited = Boolean(
              dirtyFields.firstName || dirtyFields.lastName
            );
            if (nameEdited) {
              let blank = false;
              if (!data.firstName.trim()) {
                setError('firstName', { message: 'First name is required' });
                blank = true;
              }
              if (!data.lastName.trim()) {
                setError('lastName', { message: 'Last name is required' });
                blank = true;
              }
              if (blank) throw new Error('Validation failed');
            }
            try {
              await updateUser({
                path: { user_id: userId },
                // Send a name only when one was edited. Any name in the
                // body makes the route sync the name to WorkOS, and that
                // rolls the whole update back with a 502 for a member who has
                // no WorkOS user yet, which would take an unrelated phone
                // edit down with it.
                //
                // Roger-employee status is left out rather than sent
                // unchanged for the same shape of reason: the route refuses
                // the whole update with a 403 when a caller who is not a
                // super-user includes the field at all.
                body: {
                  ...(nameEdited
                    ? {
                        first_name: data.firstName,
                        last_name: data.lastName,
                      }
                    : {}),
                  phone_number: data.phoneNumber.trim() || null,
                  ...(canEditRogerEmployee
                    ? { is_roger_employee: data.isRogerEmployee }
                    : {}),
                },
                throwOnError: true,
              });
            } catch (error) {
              console.error('Failed to update user info:', error);
              showError('Failed to update user info. Please try again.');
              throw error;
            }
          },
        }),
        [
          isDirty,
          trigger,
          getValues,
          setError,
          dirtyFields,
          userId,
          canEditRogerEmployee,
          showError,
        ]
      );

      return (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <UserCog className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>User Info</h3>
          </div>
          <div className='grid grid-cols-1 gap-3 items-start sm:grid-cols-2'>
            <div className='space-y-1'>
              <label htmlFor='edit-firstName' className='text-xs font-medium'>
                First Name
              </label>
              <Input
                id='edit-firstName'
                inputSize='sm'
                {...register('firstName')}
                aria-invalid={errors.firstName ? 'true' : 'false'}
              />
              {errors.firstName && (
                <p className='text-xs text-red-600'>
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className='space-y-1'>
              <label htmlFor='edit-lastName' className='text-xs font-medium'>
                Last Name
              </label>
              <Input
                id='edit-lastName'
                inputSize='sm'
                {...register('lastName')}
                aria-invalid={errors.lastName ? 'true' : 'false'}
              />
              {errors.lastName && (
                <p className='text-xs text-red-600'>
                  {errors.lastName.message}
                </p>
              )}
            </div>
            <div className='space-y-1 sm:col-span-2'>
              <label htmlFor='edit-phoneNumber' className='text-xs font-medium'>
                Phone number
              </label>
              <Controller
                name='phoneNumber'
                control={control}
                render={({ field }) => (
                  <Input
                    id='edit-phoneNumber'
                    inputSize='sm'
                    type='tel'
                    placeholder='555-555-5555'
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={e =>
                      field.onChange(formatUsPhoneEntry(e.target.value))
                    }
                  />
                )}
              />
              {errors.phoneNumber && (
                <p className='text-xs text-red-600'>
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          </div>
          {canEditRogerEmployee && (
            <Controller
              name='isRogerEmployee'
              control={control}
              render={({ field }) => (
                <label className='flex cursor-pointer items-center gap-2'>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={checked =>
                      field.onChange(checked === true)
                    }
                    checkboxSize='sm'
                  />
                  <span className='text-sm'>Roger employee</span>
                </label>
              )}
            />
          )}
        </div>
      );
    }
  )
);
